/*
 * JUX Header behaviour — self-contained custom elements (no global.js imports)
 * so section + CSS + JS port to a future stack as a unit.
 * State contract with section-header-jux.css: details[open], [data-open] on
 * <header-jux-drawer>, aria-expanded on triggers, .header-jux--hidden for sticky.
 */

if (!customElements.get('header-jux-disclosure')) {
  customElements.define(
    'header-jux-disclosure',
    class HeaderJuxDisclosure extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector('details');
        this.summary = this.querySelector('summary');
        if (!this.details || !this.summary) return;

        this.onDocumentClick = (event) => {
          if (this.details.open && !this.contains(event.target)) this.close();
        };
        document.addEventListener('click', this.onDocumentClick);

        this.details.addEventListener('toggle', () => {
          this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.details.open) {
            event.stopPropagation();
            this.close();
            this.summary.focus();
          }
        });

        this.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      close() {
        this.details.open = false;
      }
    }
  );
}

if (!customElements.get('header-jux-drawer')) {
  customElements.define(
    'header-jux-drawer',
    class HeaderJuxDrawer extends HTMLElement {
      connectedCallback() {
        this.toggleButton = this.querySelector('[data-drawer-toggle]');
        this.panel = this.querySelector('.header-jux__drawer');
        this.overlay = this.querySelector('[data-drawer-overlay]');
        this.closeButton = this.querySelector('[data-drawer-close]');
        if (!this.toggleButton || !this.panel) return;

        this.onKeydown = this.onKeydown.bind(this);
        this.toggleButton.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
        this.overlay?.addEventListener('click', () => this.close());
        this.closeButton?.addEventListener('click', () => this.close());

        /* Close if the viewport grows past the mobile breakpoint while open. */
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        this.onBreakpointChange = (event) => {
          if (event.matches) this.close();
        };
        this.desktopQuery.addEventListener('change', this.onBreakpointChange);
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        this.desktopQuery?.removeEventListener('change', this.onBreakpointChange);
        document.body.style.overflow = '';
      }

      get isOpen() {
        return this.hasAttribute('data-open');
      }

      open() {
        this.setAttribute('data-open', '');
        this.toggleButton.setAttribute('aria-expanded', 'true');
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeydown);
        (this.closeButton || this.panel).focus();
      }

      close() {
        if (!this.isOpen) return;
        this.removeAttribute('data-open');
        this.toggleButton.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = this.previousOverflow || '';
        document.removeEventListener('keydown', this.onKeydown);
        this.toggleButton.focus();
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          this.close();
          return;
        }
        if (event.key !== 'Tab') return;

        /* Minimal focus trap — cycle within the panel. */
        const focusables = this.panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}

if (!customElements.get('header-jux')) {
  customElements.define(
    'header-jux',
    class HeaderJux extends HTMLElement {
      connectedCallback() {
        if (!this.hasAttribute('data-sticky')) return;
        this.lastY = window.scrollY;
        this.ticking = false;
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.watchCartNotification();

        /* Publish the space the revealed bar occupies so sticky page elements
         * (e.g. the bundle panel) can offset themselves below it. */
        this.publishOffset(this.offsetHeight);
        this.resizeObserver = new ResizeObserver(() => {
          if (!this.classList.contains('header-jux--hidden')) this.publishOffset(this.offsetHeight);
        });
        this.resizeObserver.observe(this);
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        this.cartNotificationObserver?.disconnect();
        this.resizeObserver?.disconnect();
        document.documentElement.style.removeProperty('--header-jux-offset');
      }

      publishOffset(px) {
        document.documentElement.style.setProperty('--header-jux-offset', `${px}px`);
      }

      /* Dawn's cart-notification.js reveals `sticky-header` — we're not one, so
       * reveal ourselves when the toast activates or it would slide out unseen
       * behind a hidden bar. */
      watchCartNotification() {
        const notification = document.getElementById('cart-notification');
        if (!notification) return;
        this.cartNotificationObserver = new MutationObserver(() => {
          if (notification.classList.contains('active')) this.reveal();
        });
        this.cartNotificationObserver.observe(notification, { attributes: true, attributeFilter: ['class'] });
      }

      reveal() {
        this.classList.remove('header-jux--hidden');
        this.publishOffset(this.offsetHeight);
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          const y = window.scrollY;
          const goingDown = y > this.lastY;
          if (goingDown && y > this.offsetHeight + 100) {
            if (!this.classList.contains('header-jux--hidden')) {
              this.classList.add('header-jux--hidden');
              this.publishOffset(0);
              /* Never leave a dropdown floating over content while the bar hides. */
              this.querySelectorAll('details[open]').forEach((details) => (details.open = false));
            }
          } else if (!goingDown) {
            this.reveal();
          }
          this.lastY = y;
          this.ticking = false;
        });
      }
    }
  );
}
